/**
 * Phone number normalisation.
 *
 * A phone number is the admin login identifier and is globally unique, so
 * "0615550100" and "+252 61 555 0100" must resolve to the same account or the
 * uniqueness guarantee is a fiction. Every number is normalised to E.164 before
 * it is stored or looked up, and this is the only place that happens.
 *
 * Somali numbers are written a dozen ways in practice — with the leading zero,
 * without it, with 00252, with spaces and dashes — so the parser is forgiving
 * about input and strict about output.
 */

export const DEFAULT_COUNTRY_CODE = "252"; // Somalia

/**
 * Somali mobile numbers are nine digits nationally and begin 6, 7 or 9.
 *
 * Deliberately not a list of operator prefixes: they change as operators launch
 * ranges, and a stale allow-list rejects a real customer's number with no way
 * for them to understand why.
 */
const SO_NATIONAL = /^[679]\d{8}$/;

/** E.164: a plus, a country code, then up to fifteen digits in total. */
const E164 = /^\+[1-9]\d{7,14}$/;

export class PhoneError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PhoneError";
  }
}

/**
 * Returns E.164, or null when the input cannot be one.
 *
 * Null rather than throwing, because the common caller is form validation where
 * "that is not a phone number" is an expected answer rather than a fault.
 */
export function normalizePhone(
  input: string,
  countryCode: string = DEFAULT_COUNTRY_CODE,
): string | null {
  if (!input) return null;

  // Keep digits and a single leading plus; drop spaces, dashes, brackets, dots.
  const hadPlus = input.trim().startsWith("+");
  const digits = input.replace(/\D/g, "");
  if (digits.length === 0) return null;

  let national = digits;

  if (hadPlus || digits.startsWith("00")) {
    // Already international. Strip the 00 prefix if that is the form used.
    const intl = digits.startsWith("00") ? digits.slice(2) : digits;
    const candidate = `+${intl}`;
    return E164.test(candidate) ? candidate : null;
  }

  // Local forms: a leading zero is a national trunk prefix, not part of the
  // number. "0615550100" and "615550100" are the same subscriber.
  if (national.startsWith("0")) national = national.replace(/^0+/, "");

  // Somebody typed the country code without a plus.
  if (national.startsWith(countryCode) && national.length > countryCode.length) {
    national = national.slice(countryCode.length);
  }

  if (countryCode === DEFAULT_COUNTRY_CODE && !SO_NATIONAL.test(national)) {
    return null;
  }

  const candidate = `+${countryCode}${national}`;
  return E164.test(candidate) ? candidate : null;
}

/** Throwing form, for places where a bad number is a programming error. */
export function requirePhone(input: string, countryCode?: string): string {
  const normalized = normalizePhone(input, countryCode);
  if (!normalized) throw new PhoneError(`Not a valid phone number: ${input}`);
  return normalized;
}

export function isValidPhone(input: string, countryCode?: string): boolean {
  return normalizePhone(input, countryCode) !== null;
}

/**
 * For display: +252615550100 reads as +252 61 555 0100.
 *
 * Grouping is presentational only. Never store this, never look up by it.
 */
export function formatPhone(e164: string): string {
  const m = /^\+(\d{3})(\d{2})(\d{3})(\d{4})$/.exec(e164);
  if (!m) return e164;
  return `+${m[1]} ${m[2]} ${m[3]} ${m[4]}`;
}

/**
 * Partly hidden, for confirmation screens: "+252 61 *** 0100".
 *
 * Shown after a code is sent, so somebody who mistyped their number can see the
 * mistake without the full number being readable over their shoulder.
 */
export function maskPhone(e164: string): string {
  const m = /^\+(\d{3})(\d{2})(\d{3})(\d{4})$/.exec(e164);
  if (!m) return e164.replace(/\d(?=\d{4})/g, "*");
  return `+${m[1]} ${m[2]} *** ${m[4]}`;
}
